import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, ActivityIndicator, ScrollView } from 'react-native';
import { logout } from '../lib/auth';
import {
  type ResponseCount,
  type Question,
  type DeviceInfo,
  fetchAllResponseCountsSinceLastReset,
  fetchDevicesByIds,
  fetchQuestionsByIds,
} from '../lib/api';
import { ResponseCountsChart } from './ResponseCountsChart';

type Props = { onLogout?: () => void };

export function UserLoggedInScreen({ onLogout }: Props) {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<ResponseCount[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [devices, setDevices] = useState<DeviceInfo[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // fetch all counts across all devices (admin/user view)
        const data = await fetchAllResponseCountsSinceLastReset();
        if (!cancelled) setCounts(data);
        // fetch device and question details for labels
        const deviceIds = Array.from(new Set(data.map((c) => c.device)));
        const questionIds = Array.from(new Set(data.map((c) => c.question)));
        const [devs, qs] = await Promise.all([
          fetchDevicesByIds(deviceIds),
          fetchQuestionsByIds(questionIds),
        ]);
        if (!cancelled) { setDevices(devs); setQuestions(qs); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build lookup maps for rendering
  const deviceMap = new Map(devices.map((d) => [d.id, d] as const));
  const questionMap = new Map(questions.map((q) => [q.id, q] as const));
  const devicesInCounts = Array.from(new Set(counts.map((c) => c.device))).sort((a, b) => {
    const A = (deviceMap.get(a)?.name || deviceMap.get(a)?.username || a).toString().toLowerCase();
    const B = (deviceMap.get(b)?.name || deviceMap.get(b)?.username || b).toString().toLowerCase();
    return A.localeCompare(B, undefined, { numeric: true, sensitivity: 'base' });
  });

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: 'flex-start', padding: 24 }}>
      <Pressable
        onPress={() => { onLogout?.(); logout().catch(() => {}); }}
        style={{ position: 'absolute', top: 8, right: 8, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 }}
        hitSlop={{ top: 24, bottom: 24, left: 24, right: 24 }}
        pressRetentionOffset={{ top: 12, bottom: 12, left: 12, right: 12 }}
      >
        <Text style={{ color: '#888', fontSize: 16 }}>Logout</Text>
      </Pressable>
      {loading && <ActivityIndicator />}
      {!loading && (
        <View style={{ width: '100%', maxWidth: 800 }}>
          {devicesInCounts.map((devId) => {
            const dev = deviceMap.get(devId);
            const devCounts = counts.filter((c) => c.device === devId);
            const questionIds = Array.from(new Set(devCounts.map((c) => c.question)));
            return (
              <View key={devId} style={{ marginBottom: 28 }}>
                <Text style={{ fontSize: 22, fontWeight: '700', marginBottom: 10 }}>
                  Device: {dev?.name || dev?.username || dev?.id || devId}
                </Text>
                {questionIds.map((qId) => {
                  const q = questionMap.get(qId);
                  const qCounts = devCounts.filter((c) => c.question === qId);
                  return (
                    <View key={qId} style={{ marginBottom: 20, backgroundColor: '#fff', borderRadius: 12, padding: 12, shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 8 }}>
                      <Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 6 }}>{q?.text || 'Untitled question'}</Text>
                      <ResponseCountsChart counts={qCounts} options={q?.options} />
                    </View>
                  );
                })}
              </View>
            );
          })}
          {devicesInCounts.length === 0 && (
            <Text style={{ color: '#555' }}>No responses yet since last reset.</Text>
          )}
        </View>
      )}
    </ScrollView>
  );
}


